import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  TextField,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { SubmitButton } from 'components';
import { ROUTES } from 'consts';

import { useAuthLoginFormManager } from './auth-login-form.hook';

interface AuthLoginFormProps {
  next?: (data?: any) => void;
}

export const AuthLoginForm = ({ next }: AuthLoginFormProps) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const theme = useTheme();

  const [{ formManager, loading_submit }] = useAuthLoginFormManager({ next });

  const passwordHelperText = 'Passwords must be 6 characters in length';

  return (
    <form onSubmit={formManager.handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            onChange={formManager.handleChange('email')}
            error={!!(formManager.errors.email && formManager.touched.email)}
            helperText={formManager.touched.email ? formManager.errors.email : null}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" marginTop={1}>
            <Link
              variant="subtitle2"
              sx={{ textDecoration: 'none', marginBottom: 0.5, cursor: 'pointer' }}
              onClick={() => navigate(`/${ROUTES.AUTH}/${ROUTES.AUTH_FORGOT_PASSWORD}`)}
            >
              Forgot Password?
            </Link>
          </Box>

          <FormControl variant="outlined" fullWidth>
            <InputLabel>Password</InputLabel>
            <OutlinedInput
              type={showPassword ? 'text' : 'password'}
              onChange={formManager.handleChange('password')}
              endAdornment={
                <InputAdornment position="end" sx={{ marginRight: 0.8 }}>
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(event) => event.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ color: theme.palette.text.secondary }} />
                    ) : (
                      <Visibility sx={{ color: theme.palette.text.secondary }} />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
              error={!!(formManager.errors.password && formManager.touched.password)}
            />
            <FormHelperText error={!!(formManager.errors.password && formManager.touched.password)}>
              {formManager.touched.password
                ? formManager.errors.password
                  ? formManager.errors.password
                  : passwordHelperText
                : passwordHelperText}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <SubmitButton loading={loading_submit} label={'Log Me In!'} type="submit" sx={{ marginTop: 4 }} />
        </Grid>

        <Grid item xs={12}>
          <Link
            variant="subtitle2"
            onClick={() => navigate(ROUTES.HOME)}
            sx={{ textDecoration: 'none', marginTop: 3, cursor: 'pointer', display: 'inline-flex' }}
          >
            Need to create a new account?
          </Link>
        </Grid>
      </Grid>
    </form>
  );
};
